/**
 * Servicios para gestión de progreso y logros
 * Achievements (especialidades/honores) y ClassProgress (clases progresivas)
 */

import {
  where,
  orderBy,
  Timestamp,
  collection,
  query,
  getDocs,
  onSnapshot,
  doc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  getById,
  getAll,
  create,
  update,
  remove,
  subscribeToCollection,
  convertTimestamps,
} from "./base";
import type { Achievement, ClassProgress } from "@/types";

// ==================== ACHIEVEMENTS (Logros/Especialidades/Honores) ====================

export const achievementService = {
  async getById(id: string): Promise<Achievement | null> {
    return getById<Achievement>("achievements", id);
  },

  async getByPerson(personId: string): Promise<Achievement[]> {
    console.log("[WEB] achievementService.getByPerson | personId:", personId);
    const results = await getAll<Achievement>("achievements", [
      where("personId", "==", personId),
      where("status", "==", "valid"),
      orderBy("earnedDate", "desc"),
    ]);
    console.log(
      "[WEB] achievementService.getByPerson | results:",
      results.length,
      results,
    );
    return results;
  },

  async getByClub(clubId: string, periodId?: string): Promise<Achievement[]> {
    const constraints = [
      where("clubId", "==", clubId),
      where("status", "==", "valid"),
      orderBy("earnedDate", "desc"),
    ];

    if (periodId) {
      constraints.splice(1, 0, where("periodId", "==", periodId));
    }

    return getAll<Achievement>("achievements", constraints);
  },

  async getByType(
    personId: string,
    type: Achievement["type"],
  ): Promise<Achievement[]> {
    return getAll<Achievement>("achievements", [
      where("personId", "==", personId),
      where("type", "==", type),
      where("status", "==", "valid"),
      orderBy("earnedDate", "desc"),
    ]);
  },

  async create(
    data: Omit<Achievement, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<Achievement>("achievements", data, userId);
  },

  async update(id: string, data: Partial<Achievement>): Promise<void> {
    return update<Achievement>("achievements", id, data);
  },

  async delete(id: string): Promise<void> {
    return remove("achievements", id);
  },

  // Revocar un logro (no eliminar, solo marcar como revocado)
  async revoke(id: string, reason: string, userId: string): Promise<void> {
    return update<Achievement>("achievements", id, {
      status: "revoked",
      statusReason: reason,
      revokedAt: Timestamp.now(),
      revokedBy: userId,
    } as any);
  },
};

// ==================== CLASS PROGRESS (Clases Progresivas) ====================

export const classProgressService = {
  async getById(id: string): Promise<ClassProgress | null> {
    return getById<ClassProgress>("classProgress", id);
  },

  async getByPerson(personId: string): Promise<ClassProgress[]> {
    console.log("[WEB] classProgressService.getByPerson | personId:", personId);
    const results = await getAll<ClassProgress>("classProgress", [
      where("personId", "==", personId),
    ]);
    console.log(
      "[WEB] classProgressService.getByPerson | results:",
      results.length,
      results,
    );
    // Ordenar en el cliente para evitar requerir índice compuesto
    return results.sort((a, b) => {
      const dateA = a.startDate?.toDate?.() || new Date(0);
      const dateB = b.startDate?.toDate?.() || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  },

  async getByClub(clubId: string, periodId?: string): Promise<ClassProgress[]> {
    const constraints = [where("clubId", "==", clubId)];

    if (periodId) {
      constraints.push(where("periodId", "==", periodId));
    }

    const results = await getAll<ClassProgress>("classProgress", constraints);
    // Ordenar en el cliente
    return results.sort((a, b) => {
      const dateA = a.startDate?.toDate?.() || new Date(0);
      const dateB = b.startDate?.toDate?.() || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  },

  async getActiveByPerson(personId: string): Promise<ClassProgress[]> {
    return getAll<ClassProgress>("classProgress", [
      where("personId", "==", personId),
      where("status", "==", "in_progress"),
    ]);
  },

  /**
   * Verificar si ya existe un progreso de clase para esta persona+clase
   */
  async existsForPersonAndClass(
    personId: string,
    classId: string,
  ): Promise<ClassProgress | null> {
    const results = await getAll<ClassProgress>("classProgress", [
      where("personId", "==", personId),
      where("classId", "==", classId),
    ]);
    return results.length > 0 ? results[0] : null;
  },

  /**
   * Crear progreso de clase con protección atómica contra duplicados.
   *
   * Usa ID determinístico (clubId_personId_classId) + verificación de docs legacy
   * para garantizar que no se puedan crear duplicados.
   *
   * IMPORTANTE: Incluye clubId porque una persona puede estar en múltiples clubes
   * y hacer la misma clase en cada uno.
   */
  async create(
    data: Omit<ClassProgress, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    // Validación de campos requeridos para evitar IDs mal formados
    if (!data.clubId || !data.personId || !data.classId) {
      throw new Error("Faltan campos requeridos: clubId, personId o classId");
    }

    // Verificación LEGACY: buscar documentos con IDs automáticos (pre-fix)
    // Esto cubre documentos creados antes del fix con IDs como "Xk9mP2q..."
    const legacyExisting = await classProgressService.existsForPersonAndClass(
      data.personId,
      data.classId,
    );
    if (legacyExisting && legacyExisting.clubId === data.clubId) {
      throw new Error(
        `Ya existe un progreso de clase "${data.className || data.classId}" para este miembro (estado: ${legacyExisting.status})`,
      );
    }

    // ID determinístico: clubId_personId_classId
    // Permite que la misma persona haga la misma clase en diferentes clubes
    const progressId = `${data.clubId}_${data.personId}_${data.classId}`;
    const progressRef = doc(db, "classProgress", progressId);

    await runTransaction(db, async (tx) => {
      // Verificar si ya existe el documento con ID determinístico
      const existingSnap = await tx.get(progressRef);
      if (existingSnap.exists()) {
        const existing = existingSnap.data();
        throw new Error(
          `Ya existe un progreso de clase "${data.className || data.classId}" para este miembro (estado: ${existing.status})`,
        );
      }

      // Crear el documento
      tx.set(progressRef, {
        ...data,
        id: progressId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        updatedBy: userId,
      });
    });

    return progressId;
  },

  async update(id: string, data: Partial<ClassProgress>): Promise<void> {
    return update<ClassProgress>("classProgress", id, data);
  },

  async delete(id: string): Promise<void> {
    return remove("classProgress", id);
  },

  // Actualizar requisito completado
  async updateRequirement(
    id: string,
    requirementId: string,
    completed: boolean,
    userId: string,
  ): Promise<void> {
    const progress = await classProgressService.getById(id);
    if (!progress) throw new Error("Class progress not found");

    const requirements = progress.requirements.map((req) => {
      if (req.requirementId === requirementId) {
        return {
          ...req,
          completed,
          completedAt: completed ? Timestamp.now() : undefined,
          completedBy: completed ? userId : undefined,
        };
      }
      return req;
    });

    // Calcular porcentaje de completitud
    const completedCount = requirements.filter((r) => r.completed).length;
    const completionPercentage = Math.round(
      (completedCount / requirements.length) * 100,
    );

    await classProgressService.update(id, {
      requirements,
      completionPercentage,
    });
  },

  // Marcar clase como completada
  async markCompleted(id: string, _userId: string): Promise<void> {
    return update<ClassProgress>("classProgress", id, {
      status: "completed",
      completedDate: Timestamp.now(),
    });
  },

  // Marcar clase como investida
  async markInvested(id: string, _userId: string): Promise<void> {
    return update<ClassProgress>("classProgress", id, {
      status: "invested",
      investedDate: Timestamp.now(),
    });
  },

  // Enviar a aprobación por la asociación
  async submitForApproval(id: string, userId: string): Promise<void> {
    const progress = await classProgressService.getById(id);
    if (!progress) throw new Error("Class progress not found");
    if (progress.completionPercentage !== 100) {
      throw new Error(
        "La clase debe estar 100% completada para enviar a aprobación",
      );
    }

    return update<ClassProgress>("classProgress", id, {
      status: "pending_approval",
      submittedForApprovalAt: Timestamp.now(),
      submittedBy: userId,
    });
  },

  // Aprobar clase (solo director de asociación)
  async approveClass(id: string, userId: string): Promise<void> {
    const progress = await classProgressService.getById(id);
    if (!progress) throw new Error("Class progress not found");
    if (progress.status !== "pending_approval") {
      throw new Error("La clase debe estar pendiente de aprobación");
    }

    return update<ClassProgress>("classProgress", id, {
      status: "approved",
      approvedAt: Timestamp.now(),
      approvedBy: userId,
    });
  },

  // Rechazar clase (solo director de asociación)
  async rejectClass(id: string, userId: string, reason: string): Promise<void> {
    const progress = await classProgressService.getById(id);
    if (!progress) throw new Error("Class progress not found");
    if (progress.status !== "pending_approval") {
      throw new Error("La clase debe estar pendiente de aprobación");
    }

    return update<ClassProgress>("classProgress", id, {
      status: "rejected",
      rejectedAt: Timestamp.now(),
      rejectedBy: userId,
      rejectionReason: reason,
    });
  },

  // Obtener clases pendientes de aprobación por asociación
  async getPendingApprovalByAssociation(
    associationId: string,
  ): Promise<ClassProgress[]> {
    // Primero obtenemos los clubIds de la asociación
    const clubsSnap = await getDocs(
      query(
        collection(db, "clubs"),
        where("associationId", "==", associationId),
      ),
    );
    const clubIds = clubsSnap.docs.map((d) => d.id);

    if (clubIds.length === 0) return [];

    // Firestore limita 'in' a 30 elementos, así que hacemos chunks si es necesario
    const results: ClassProgress[] = [];
    const chunkSize = 30;

    for (let i = 0; i < clubIds.length; i += chunkSize) {
      const chunk = clubIds.slice(i, i + chunkSize);
      const chunkResults = await getAll<ClassProgress>("classProgress", [
        where("clubId", "in", chunk),
        where("status", "==", "pending_approval"),
      ]);
      results.push(...chunkResults);
    }

    return results.sort((a, b) => {
      const dateA = a.submittedForApprovalAt?.toDate?.() || new Date(0);
      const dateB = b.submittedForApprovalAt?.toDate?.() || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  },

  // Sincronizar requisitos del club a progresos existentes (solo en progreso)
  async syncRequirementsToExisting(
    clubId: string,
    classId: string,
    requirements: {
      id: string;
      name: string;
      description?: string;
      order: number;
    }[],
    userId: string,
  ): Promise<number> {
    // Obtener progresos en estado "in_progress" para esta clase y club
    const progresses = await getAll<ClassProgress>("classProgress", [
      where("clubId", "==", clubId),
      where("classId", "==", classId),
      where("status", "==", "in_progress"),
    ]);

    let updatedCount = 0;

    for (const progress of progresses) {
      // Crear mapa de requisitos existentes completados
      const existingCompletedMap = new Map<
        string,
        { completed: boolean; completedAt?: any; completedBy?: string }
      >();
      progress.requirements?.forEach((req) => {
        if (req.completed) {
          // Usar el nombre del requisito como clave para mantener el estado
          existingCompletedMap.set(req.name.toLowerCase().trim(), {
            completed: req.completed,
            completedAt: req.completedAt,
            completedBy: req.completedBy,
          });
        }
      });

      // Crear nuevos requisitos, preservando estado de completado si el nombre coincide
      const newRequirements = requirements.map((req) => {
        const existingState = existingCompletedMap.get(
          req.name.toLowerCase().trim(),
        );
        return {
          requirementId: req.id,
          name: req.name,
          completed: existingState?.completed || false,
          completedAt: existingState?.completedAt,
          completedBy: existingState?.completedBy,
        };
      });

      // Calcular porcentaje
      const completedCount = newRequirements.filter((r) => r.completed).length;
      const completionPercentage =
        newRequirements.length > 0
          ? Math.round((completedCount / newRequirements.length) * 100)
          : 0;

      await update<ClassProgress>("classProgress", progress.id, {
        requirements: newRequirements,
        completionPercentage,
        updatedBy: userId,
      });

      updatedCount++;
    }

    return updatedCount;
  },

  // Obtener progresos por clase y club (para contar cuántos se actualizarán)
  async getByClubAndClass(
    clubId: string,
    classId: string,
  ): Promise<ClassProgress[]> {
    return getAll<ClassProgress>("classProgress", [
      where("clubId", "==", clubId),
      where("classId", "==", classId),
      where("status", "==", "in_progress"),
    ]);
  },
};

// ==================== REALTIME SUBSCRIPTIONS ====================

export function subscribeToAchievementsByClub(
  clubId: string,
  periodId: string | undefined,
  callback: (achievements: Achievement[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const constraints = [
    where("clubId", "==", clubId),
    where("status", "==", "valid"),
    orderBy("earnedDate", "desc"),
  ];
  if (periodId) {
    constraints.splice(1, 0, where("periodId", "==", periodId));
  }
  return subscribeToCollection<Achievement>(
    "achievements",
    callback,
    constraints,
    onError,
  );
}

export function subscribeToClassProgressByClub(
  clubId: string,
  periodId: string | undefined,
  callback: (progress: ClassProgress[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const constraints = [where("clubId", "==", clubId)];
  if (periodId) {
    constraints.push(where("periodId", "==", periodId));
  }
  return subscribeToCollection<ClassProgress>(
    "classProgress",
    (items) => {
      // Ordenar en el cliente (mismo que la versión síncrona)
      const sorted = [...items].sort((a, b) => {
        const dateA = (a.startDate as any)?.toDate?.() || new Date(0);
        const dateB = (b.startDate as any)?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });
      callback(sorted);
    },
    constraints,
    onError,
  );
}

export function subscribeToAchievementsByPerson(
  personId: string,
  callback: (achievements: Achievement[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Achievement>(
    "achievements",
    callback,
    [
      where("personId", "==", personId),
      where("status", "==", "valid"),
      orderBy("earnedDate", "desc"),
    ],
    onError,
  );
}

export function subscribeToAchievementsByType(
  personId: string,
  type: Achievement["type"],
  callback: (achievements: Achievement[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Achievement>(
    "achievements",
    callback,
    [
      where("personId", "==", personId),
      where("type", "==", type),
      where("status", "==", "valid"),
      orderBy("earnedDate", "desc"),
    ],
    onError,
  );
}

export function subscribeToAchievement(
  id: string,
  callback: (achievement: Achievement | null) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, "achievements", id),
    (docSnap) => {
      if (docSnap.exists()) {
        callback(
          convertTimestamps({
            id: docSnap.id,
            ...docSnap.data(),
          }) as Achievement,
        );
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error("[Firestore] subscribeToAchievement error:", error);
      if (onError) onError(error);
    },
  );
}

export function subscribeToClassProgressByPerson(
  personId: string,
  callback: (progress: ClassProgress[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<ClassProgress>(
    "classProgress",
    (items) => {
      const sorted = [...items].sort((a, b) => {
        const dateA = (a.startDate as any)?.toDate?.() || new Date(0);
        const dateB = (b.startDate as any)?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });
      callback(sorted);
    },
    [where("personId", "==", personId)],
    onError,
  );
}

export function subscribeToActiveClassProgress(
  personId: string,
  callback: (progress: ClassProgress[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<ClassProgress>(
    "classProgress",
    callback,
    [where("personId", "==", personId), where("status", "==", "in_progress")],
    onError,
  );
}

export function subscribeToClassProgress(
  id: string,
  callback: (progress: ClassProgress | null) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, "classProgress", id),
    (docSnap) => {
      if (docSnap.exists()) {
        callback(
          convertTimestamps({
            id: docSnap.id,
            ...docSnap.data(),
          }) as ClassProgress,
        );
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error("[Firestore] subscribeToClassProgress error:", error);
      if (onError) onError(error);
    },
  );
}
